import { Link } from "react-router-dom";
import { Star, ArrowUpRight, Sparkles } from "lucide-react";
import { Logo } from "./landing/Logo";
import CHANGELOG from "../data/changelog";

function openCookiePrefs(e) {
  e.preventDefault();
  try {
    window.dispatchEvent(new CustomEvent("seointent:open-cookie-prefs"));
  } catch (err) {}
}

const COLUMNS = [
  {
    title: "Company",
    links: [
      { label: "Pricing", to: "/pricing" },
      { label: "For Agencies", to: "/for-agencies" },
      { label: "Affiliates", to: "/affiliates" },
      { label: "SaaS Affiliate Program", to: "/saas-affiliate-program" },
      { label: "Integrations", to: "/integrations" },
      { label: "Sign in", to: "/login" },
      { label: "Start free trial", to: "/register" },
    ],
  },
  {
    title: "Platform",
    links: [
      { label: "AI Citation Tracking", to: "/ai-citation-tracking" },
      { label: "GEO Optimization", to: "/geo-optimization" },
      { label: "Keyword Clustering", to: "/keyword-clustering" },
      { label: "SEO Automation", to: "/seo-automation" },
      { label: "Topical Authority", to: "/topical-authority" },
      { label: "Search Intent Tool", to: "/search-intent-tool" },
      { label: "AI SEO Tools", to: "/ai-seo-tools" },
      { label: "All Features", to: "/features" },
    ],
  },
  {
    title: "Solutions",
    links: [
      { label: "For SaaS", to: "/ai-seo-for-saas" },
      { label: "For eCommerce", to: "/ai-seo-for-ecommerce" },
      { label: "For Marketing Agencies", to: "/ai-seo-for-marketing-agencies" },
      { label: "For Real Estate", to: "/ai-seo-for-real-estate" },
      { label: "For Healthcare", to: "/ai-seo-for-healthcare" },
      { label: "For Law Firms", to: "/ai-seo-for-law-firms" },
      { label: "For Restaurants", to: "/ai-seo-for-restaurants" },
      { label: "For Dentists", to: "/ai-seo-for-dentists" },
      { label: "For Coaches", to: "/ai-seo-for-coaches" },
      { label: "For B2B", to: "/ai-seo-for-b2b" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Blog", to: "/blog" },
      { label: "Changelog", to: "/changelog", badge: "New" },
      { label: "Resources Hub", to: "/resources" },
      { label: "AI Tool Use Cases", to: "/use-cases", badge: "New" },
      { label: "SEO Guide", to: "/seo-guide" },
      { label: "GEO Guide", to: "/geo-guide" },
      { label: "What Is Search Intent", to: "/what-is-search-intent" },
      { label: "GEO vs SEO", to: "/geo-vs-seo" },
      { label: "How To Rank In ChatGPT", to: "/how-to-rank-in-chatgpt" },
      { label: "AI Citation Tracking Guide", to: "/ai-citation-tracking-guide" },
      { label: "AI Visibility Checker", to: "/tools/ai-visibility-checker", badge: "Free" },
      { label: "AI Content Detector", to: "/tools/ai-content-detector", badge: "New" },
      { label: "Schema Markup Generator", to: "/tools/schema-generator", badge: "Free" },
      { label: "Meta Tag Analyzer", to: "/tools/meta-tag-analyzer", badge: "Free" },
      { label: "Sitemap & Robots Analyzer", to: "/tools/sitemap-analyzer", badge: "Free" },
    ],
  },
  {
    title: "Compare",
    links: [
      { label: "vs Ahrefs", to: "/ahrefs-alternative" },
      { label: "vs Semrush", to: "/semrush-alternative" },
      { label: "vs Surfer SEO", to: "/surfer-seo-alternative" },
      { label: "vs Frase", to: "/vs/frase" },
      { label: "vs MarketMuse", to: "/vs/marketmuse" },
      { label: "vs Clearscope", to: "/vs/clearscope" },
      { label: "vs Neuronwriter", to: "/vs/neuronwriter" },
      { label: "Jasper Alternative", to: "/jasper-alternative" },
      { label: "Copy.ai Alternative", to: "/copy-ai-alternative" },
      { label: "Outranking Alternative", to: "/outranking-alternative" },
    ],
  },
];

const REVIEWS = [
  { source: "G2", score: "4.8", count: "120+ reviews" },
  { source: "Capterra", score: "4.9", count: "80+ reviews" },
  { source: "Product Hunt", score: "4.7", count: "200+ upvotes" },
];

export default function MegaFooter() {
  return (
    <footer className="bg-white border-t border-zinc-200">
      {/* Top row: logo + reviews */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-8 border-b border-zinc-200">
          <Logo />
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            {REVIEWS.map((r) => (
              <div key={r.source} className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
                </div>
                <span className="font-satoshi text-sm font-bold text-zinc-900">{r.score}/5</span>
                <span className="font-satoshi text-xs text-zinc-500">{r.source} · {r.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Mega columns */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-10 mt-10">
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h3 className="font-cabinet font-bold text-sm text-zinc-900 mb-4">{col.title}</h3>
              <ul className="space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="font-satoshi text-sm text-zinc-600 hover:text-[#FF2F2F] transition-colors inline-flex items-center gap-1.5"
                    >
                      {link.label}
                      {link.badge && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded bg-emerald-50 text-emerald-700 border-emerald-200">
                          {link.badge}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* "What's new" teaser — latest changelog entry, links to /changelog */}
      {CHANGELOG[0] && (
        <div className="border-t border-zinc-200 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <Link
              to="/changelog"
              className="group flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
              data-testid="footer-changelog-teaser"
            >
              <div className="flex items-center gap-2 flex-shrink-0">
                <Sparkles className="w-4 h-4 text-[#FF2F2F]" />
                <span className="font-mono-data text-[10px] tracking-[0.2em] uppercase font-bold text-[#FF2F2F]">What's new</span>
                <span className="font-mono-data text-[10px] tracking-[0.15em] uppercase text-zinc-400 hidden sm:inline">
                  {new Date(CHANGELOG[0].date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </span>
              </div>
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <span className="font-satoshi text-sm text-zinc-700 group-hover:text-[#FF2F2F] transition-colors truncate">
                  <span className="font-semibold">{CHANGELOG[0].title}</span>
                  <span className="text-zinc-500"> — {CHANGELOG[0].benefit}</span>
                </span>
                <ArrowUpRight className="w-3.5 h-3.5 text-zinc-400 group-hover:text-[#FF2F2F] flex-shrink-0 transition-colors" />
              </div>
              <span className="font-satoshi text-xs font-semibold text-[#FF2F2F] flex-shrink-0 hidden md:inline">View changelog →</span>
            </Link>
          </div>
        </div>
      )}

      {/* Legal strip */}
      <div className="border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
          <Link to="/terms-of-service" className="font-satoshi text-zinc-500 hover:text-[#FF2F2F]">Terms of Service</Link>
          <Link to="/privacy-policy" className="font-satoshi text-zinc-500 hover:text-[#FF2F2F]">Privacy Policy</Link>
          <Link to="/refund-policy" className="font-satoshi text-zinc-500 hover:text-[#FF2F2F]">Refund Policy</Link>
          <Link to="/cookie-policy" className="font-satoshi text-zinc-500 hover:text-[#FF2F2F]">Cookie Policy</Link>
          <button
            type="button"
            onClick={openCookiePrefs}
            className="font-satoshi text-zinc-500 hover:text-[#FF2F2F] bg-transparent border-0 p-0 cursor-pointer"
          >
            Cookie Preferences
          </button>
        </div>
      </div>

      {/* Bottom strip */}
      <div className="border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="font-satoshi text-xs text-zinc-500">
            © {new Date().getFullYear()} SEOintent. The AI SEO platform built for the AI search era.
          </p>
          <div className="flex items-center gap-5 text-xs">
            <Link to="/blog" className="font-satoshi text-zinc-500 hover:text-[#FF2F2F]">Blog</Link>
            <Link to="/changelog" className="font-satoshi text-zinc-500 hover:text-[#FF2F2F]">Changelog</Link>
            <Link to="/pricing" className="font-satoshi text-zinc-500 hover:text-[#FF2F2F]">Pricing</Link>
            <Link to="/login" className="font-satoshi text-zinc-500 hover:text-[#FF2F2F]">Sign in</Link>
            <Link to="/register" className="font-satoshi font-semibold text-[#FF2F2F] inline-flex items-center gap-1">
              Start free trial <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
